// src/api.js
import axios from "axios";
import { auth } from "./firebase"; // <-- to read current user token
import { WORLD_CUP_CODE, WORLD_CUP_SEASON, isWorldCupCompetition } from "./utils/worldCup";

const ONRENDER_HOST = "logies-edges-api.onrender.com";

export const ensureHttpsOnrenderUrl = (rawValue) => {
  const value = String(rawValue || "").trim();
  if (!value) return value;

  try {
    const normalizedInput =
      value.startsWith("//") || /^[a-z]+:\/\//i.test(value) ? value : `https://${value}`;
    const url = new URL(normalizedInput);
    if (url.hostname === ONRENDER_HOST) {
      url.protocol = "https:";
    }
    return url.toString().replace(/\/$/, "");
  } catch {
    return value.replace(`http://${ONRENDER_HOST}`, `https://${ONRENDER_HOST}`);
  }
};

export const normalizeApiBase = (rawBase) => {
  const fallback = `https://${ONRENDER_HOST}`;
  const value = String(rawBase || fallback).trim();
  if (!value) return fallback;
  const normalized = ensureHttpsOnrenderUrl(value);
  return normalized || fallback;
};

export const API_BASE = normalizeApiBase(process.env.REACT_APP_API_BASE);

export const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

export const fetchDailyPlayerGame = async () => (await api.get("/api/games/daily-player/today")).data;
export const searchDailyPlayerGamePlayers = async (q) => (await api.get("/api/games/daily-player/players", { params: { q, limit: 12 } })).data;
export const submitDailyPlayerGuess = async (playerId, priorPlayerIds = []) => (await api.post("/api/games/daily-player/guess", {
  player_id: playerId,
  prior_player_ids: priorPlayerIds,
})).data;
export const setDailyPlayerGameAnswer = async (playerId, force = false) => (await api.put("/api/games/daily-player/today", { player_id: playerId, force })).data;
export const fetchAdminDailyPlayerGame = async () => (await api.get("/api/games/daily-player/admin/today")).data;
export const notifyDailyPlayerGame = async () => (await api.post("/api/games/daily-player/notify")).data;
export const fetchDailyPlayerQueue = async (days = 30) => (await api.get("/api/games/daily-player/admin/queue", { params: { days } })).data;
export const queueDailyPlayerGame = async (playerId, puzzleDate) => (await api.post("/api/games/daily-player/admin/queue", {
  player_id: playerId,
  puzzle_date: puzzleDate,
})).data;
export const removeDailyPlayerFromQueue = async (puzzleDate) => (await api.delete(`/api/games/daily-player/admin/queue/${puzzleDate}`)).data;

const FIXTURE_EDGE_DEBUG = true;

const clientCacheStore = new Map();
const clientCacheInflight = new Map();
const CLIENT_CACHE_PREFIX = "csb_api_cache_v1";

const stableParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== null && value !== "")
      .sort(([a], [b]) => a.localeCompare(b))
  );

const clientCacheKey = (scope, params = {}) =>
  `${CLIENT_CACHE_PREFIX}:${scope}:${JSON.stringify(stableParams(params))}`;

const readClientCache = (key, ttlMs) => {
  const now = Date.now();
  const memEntry = clientCacheStore.get(key);
  if (memEntry && now - memEntry.ts < ttlMs) return memEntry.value;

  if (typeof window === "undefined" || !window.sessionStorage) return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || now - Number(parsed.ts || 0) >= ttlMs) {
      window.sessionStorage.removeItem(key);
      return null;
    }
    clientCacheStore.set(key, parsed);
    return parsed.value;
  } catch {
    return null;
  }
};

const writeClientCache = (key, value) => {
  const entry = { ts: Date.now(), value };
  clientCacheStore.set(key, entry);
  if (typeof window === "undefined" || !window.sessionStorage) return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // ignore storage quota / serialization issues
  }
};

const cachedRequest = async ({ scope, ttlMs, loader, params = {} }) => {
  const key = clientCacheKey(scope, params);
  const cached = readClientCache(key, ttlMs);
  if (cached != null) return cached;

  if (clientCacheInflight.has(key)) {
    return clientCacheInflight.get(key);
  }

  const pending = Promise.resolve()
    .then(loader)
    .then((value) => {
      writeClientCache(key, value);
      clientCacheInflight.delete(key);
      return value;
    })
    .catch((error) => {
      clientCacheInflight.delete(key);
      throw error;
    });

  clientCacheInflight.set(key, pending);
  return pending;
};

// 👇 keep this – handy for debugging in the browser
if (typeof window !== "undefined") {
  window.api = api;
}

// -----------------------------
// Attach Firebase ID token
// -----------------------------
api.interceptors.request.use(async (config) => {
  config.baseURL = normalizeApiBase(config.baseURL || API_BASE);
  if (typeof config.url === "string" && config.url.startsWith("http")) {
    config.url = ensureHttpsOnrenderUrl(config.url);
  }

  const u = auth?.currentUser;
  if (u) {
    try {
      const token = await u.getIdToken();
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    } catch {
      // ignore token errors
    }
  }
  return config;
});

const buildFirebaseAuthHeaders = async ({ forceRefresh = false } = {}) => {
  const firebaseUser = auth?.currentUser || null;
  if (!firebaseUser) {
    return {
      firebaseUser: null,
      token: "",
      headers: {},
      forceRefresh,
    };
  }

  try {
    const token = await firebaseUser.getIdToken(forceRefresh);
    return {
      firebaseUser,
      token: token || "",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      forceRefresh,
    };
  } catch (error) {
    if (FIXTURE_EDGE_DEBUG) {
      console.info("[FixtureEdges] token error", {
        forceRefresh,
        message: error?.message || String(error),
      });
    }
    return {
      firebaseUser,
      token: "",
      headers: {},
      forceRefresh,
    };
  }
};

// -----------------------------
// Handle 401 gracefully (NO redirect for public pages)
// -----------------------------
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) {
      // Many routes are public; just bubble up the error
      return Promise.reject(err);
    }
    return Promise.reject(err);
  }
);

// -----------------------------
// Auth: /auth/me
// -----------------------------
export const fetchCurrentUser = async ({ forceRefresh = false } = {}) => {
  const authState = await buildFirebaseAuthHeaders({ forceRefresh });
  const res = await api.get("/auth/me", {
    headers: authState.headers,
  });
  return res.data;
};

// -----------------------------
// Referrals
// -----------------------------
export const fetchMyReferrals = () =>
  api.get("/api/referrals/me").then((r) => r.data);

export const claimReferralCode = (code) =>
  api.post("/api/referrals/claim", { code }).then((r) => r.data);

// 👇 NEW: trigger welcome-email test for the logged-in user
export const sendTestWelcomeEmail = () =>
  api.post("/auth/welcome-email/test").then((r) => r.data);

// -----------------------------
// Email preferences
// -----------------------------
export const updateEmailPreferences = (email_picks_opt_in) =>
  api
    .post("/auth/email-preferences", { email_picks_opt_in })
    .then((r) => r.data);

export const sendFeaturedPicksEmail = async ({ day, premiumOnly = false }) => {
  const res = await api.post("/api/admin/email/featured-picks", null, {
    params: {
      day,
      premium_only: premiumOnly,
      world_cup_matchday: false,
    },
  });
  return res.data;
};

// -----------------------------
// Admin: Community / Featured 5
// -----------------------------
export const fetchAdminFeaturedFixtures = async (day) => {
  const res = await api.get("/api/social/admin/featured-fixtures", {
    params: { day },
  });
  return res.data;
};

export const fetchFeaturedFixtures = async (day) => {
  const res = await api.get("/api/social/featured-fixtures", {
    params: { day },
  });
  return res.data;
};

export const updateAdminFeaturedFixtures = async ({ day, fixture_ids }) => {
  const res = await api.put("/api/social/admin/featured-fixtures", {
    day,
    fixture_ids,
  });
  return res.data;
};

export const notifyAdminFeaturedFixtures = async ({ day } = {}) => {
  const res = await api.post("/api/social/admin/featured-fixtures/notify", {
    day,
  });
  return res.data;
};

export const fetchCommunityHome = async () => {
  const res = await api.get("/api/social/community-home");
  return res.data;
};

export const fetchSocialAdminMonitor = async ({
  days = 7,
  fixture_limit = 12,
  recent_comments_limit = 20,
  leaderboard_limit = 10,
} = {}) => {
  const params = {
    days,
    fixture_limit,
    recent_comments_limit,
    leaderboard_limit,
  };
  const authState = await buildFirebaseAuthHeaders();
  const url = api.getUri({ url: "/api/social/admin/monitor", params });
  const config = {
    params,
    headers: {
      ...authState.headers,
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
  };

  try {
    const res = await api.get("/api/social/admin/monitor", config);
    if (process.env.NODE_ENV !== "production") {
      console.info("[AdminSocialMonitor] request", {
        url,
        status: res.status,
        firebaseBearerAttached: Boolean(authState.token),
      });
      console.info("[AdminSocialMonitor] raw response", res.data);
    }
    return res.data;
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.info("[AdminSocialMonitor] request failed", {
        url,
        status: error?.response?.status || "network-error",
        firebaseBearerAttached: Boolean(authState.token),
        raw: error?.response?.data || null,
      });
    }
    throw error;
  }
};

// -----------------------------
// Admin: Featured picks
// -----------------------------
export const fetchAdminPicks = async ({ day, sport } = {}) => {
  const res = await api.get("/api/admin/picks", {
    params: { day, ...(sport ? { sport } : {}) },
  });
  return res.data;
};

export const createAdminPick = async (payload) => {
  const res = await api.post("/api/admin/picks", payload);
  return res.data;
};

export const deleteAdminPick = async (pickId) => {
  const res = await api.delete(`/api/admin/picks/${pickId}`);
  return res.data;
};

export const fetchAdminPickFixtures = async ({ day, sport } = {}) => {
  const requestSport = sport === "football" ? "soccer" : sport;
  const res = await api.get("/api/admin/picks/fixtures", {
    params: { day, sport: requestSport },
  });
  return res.data;
};

// -----------------------------
// Admin: Featured accas
// -----------------------------
export const fetchAdminAccas = async ({ day } = {}) => {
  const res = await api.get("/admin/accas", {
    params: { day },
  });
  return res.data;
};

export const createAdminAcca = async (payload) => {
  const res = await api.post("/admin/accas/create", payload);
  return res.data;
};

export const publishAdminAcca = async ({ ticketId, isPublic = true }) => {
  const res = await api.post(`/admin/accas/${ticketId}/publish`, null, {
    params: { public: isPublic ? 1 : 0 },
  });
  return res.data;
};

export const deleteAdminAcca = async (ticketId) => {
  const res = await api.delete(`/admin/accas/${ticketId}`);
  return res.data;
};

export const settleAdminAcca = async ({ ticketId, result, profit = null, legs = [] }) => {
  const res = await api.post(`/admin/accas/${ticketId}/settle`, { result, profit, legs });
  return res.data;
};

// -----------------------------
// Public helpers
// -----------------------------
export const fetchShortlist = () =>
  api.get("/shortlist").then((r) => r.data);

export const fetchDailyFixtures = (day, sport = "all") =>
  api
    .get("/api/public/fixtures/daily", { params: { day, sport } })
    .then((r) => r.data);

export const fetchFixtureJson = (fixtureId) =>
  cachedRequest({
    scope: "fixture_json",
    ttlMs: 60 * 1000,
    params: { fixtureId },
    loader: () => api.get(`/api/fixtures/id/${fixtureId}/json`).then((r) => r.data),
  });

export const fetchBaseballFixtureContext = (fixtureId) =>
  cachedRequest({
    scope: "baseball_fixture_context",
    ttlMs: 2 * 60 * 1000,
    params: { fixtureId },
    loader: () =>
      api
        .get("/baseball/fixture-context", { params: { fixture_id: fixtureId } })
        .then((r) => r.data),
  });

export const fetchBaseballFixtureSummary = (fixtureId) =>
  cachedRequest({
    scope: "baseball_fixture_summary",
    ttlMs: 5 * 60 * 1000,
    params: { fixtureId },
    loader: () =>
      api
        .get("/baseball/fixture-summary", { params: { fixture_id: fixtureId } })
        .then((r) => r.data),
  });

export const fetchBaseballTeamRoster = ({ teamId, season = 2026 }) =>
  cachedRequest({
    scope: "baseball_team_roster",
    ttlMs: 30 * 60 * 1000,
    params: { teamId, season },
    loader: () =>
      api
        .get("/baseball/team-roster", { params: { team_id: teamId, season } })
        .then((r) => r.data),
  });

export const fetchBaseballPlayerProfile = ({ playerId, season = 2026 }) =>
  cachedRequest({
    scope: "baseball_player_profile_v2",
    ttlMs: 30 * 60 * 1000,
    params: { playerId, season },
    loader: () =>
      api
        .get("/baseball/player-profile", { params: { player_id: playerId, season } })
        .then((r) => r.data),
  });

export const fetchBaseballPlayerOverview = ({ playerId, season = 2026, teamId } = {}) =>
  cachedRequest({
    scope: "baseball_player_overview_v2",
    ttlMs: 15 * 60 * 1000,
    params: { playerId, season, teamId },
    loader: () =>
      api
        .get("/baseball/player-overview", {
          params: {
            player_id: playerId,
            season,
            team_id: teamId,
          },
        })
        .then((r) => r.data),
  });

export const fetchBaseballFixtureH2H = ({ fixtureId, seasonsBack = 1 } = {}) =>
  cachedRequest({
    scope: "baseball_fixture_h2h",
    ttlMs: 15 * 60 * 1000,
    params: { fixtureId, seasonsBack },
    loader: () =>
      api
        .get("/baseball/fixture-h2h", {
          params: {
            fixture_id: fixtureId,
            seasons_back: seasonsBack,
          },
        })
        .then((r) => r.data),
  });

export const fetchPlayerOdds = ({ fixtureId, market, bookmaker, player, limit } = {}) =>
  cachedRequest({
    scope: "baseball_player_odds",
    ttlMs: 60 * 1000,
    params: { fixtureId, market, bookmaker, player, limit },
    loader: () =>
      api
        .get("/api/player-odds/", {
          params: {
            fixture_id: fixtureId,
            market,
            bookmaker,
            player,
            limit,
          },
        })
        .then((r) => r.data),
  });

// -----------------------------
// Tipsters Platform
// -----------------------------
export const fetchTipsters = ({ sort = "roi_30d_desc", sport } = {}) =>
  api.get("/api/tipsters", { params: { sort, sport } }).then((r) => r.data);

// Get logged-in user's own tipster profile
export const fetchMyTipster = async () => {
  const res = await api.get("/api/tipsters/me");
  return res.data || null;
};

export const fetchTipster = (username) =>
  api
    .get(`/api/tipsters/${encodeURIComponent(username)}`)
    .then((r) => r.data);

export const fetchTipsterPicks = (username, { limit = 50, settled } = {}) =>
  api
    .get(`/api/tipsters/${encodeURIComponent(username)}/picks`, {
      params: { limit, settled },
    })
    .then((r) => r.data);

export const tipstersCreate = (payload) =>
  api.post("/api/tipsters", payload).then((r) => r.data);

// ----- Picks -----
export const createTipsterPick = (username, payload) =>
  api
    .post(`/api/tipsters/${encodeURIComponent(username)}/picks`, payload)
    .then((r) => r.data);

export const settleTipsterPick = (pickId, result) =>
  api
    .post(`/api/tipsters/picks/${pickId}/settle`, { result })
    .then((r) => r.data);

export const deleteTipsterPick = (pickId) =>
  api.delete(`/api/tipsters/picks/${pickId}`).then((r) => r.data);

// ----- Fixtures (single) -----
export const fetchFixture = (id) =>
  api.get(`/api/fixtures/${id}`).then((r) => r.data);

// ----- 🔐 Fixture edges (freemium gating) -----
export const fetchFixtureEdges = async (fixtureId) => {
  const path = `/fixture/${fixtureId}/edges`;
  const initialAuth = await buildFirebaseAuthHeaders();

  if (FIXTURE_EDGE_DEBUG) {
    console.info("[FixtureEdges] request:start", {
      fixtureId,
      path,
      signedIn: !!initialAuth.firebaseUser,
      tokenAttached: !!initialAuth.token,
      forcedRefresh: false,
    });
  }

  let res = await api.get(path, {
    headers: {
      ...initialAuth.headers,
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
  });

  let data = res?.data || {};
  let me = null;
  let retriedWithFreshToken = false;

  if (FIXTURE_EDGE_DEBUG) {
    console.info("[FixtureEdges] response:first", {
      fixtureId,
      isPremium: !!data?.is_premium,
      hasAccess: !!data?.has_access,
      usedToday: data?.used_today ?? null,
      limit: data?.limit ?? null,
      signedIn: !!initialAuth.firebaseUser,
    });
  }

  if (initialAuth.firebaseUser && !data?.is_premium && !data?.has_access) {
    try {
      me = await fetchCurrentUser();
      if (FIXTURE_EDGE_DEBUG) {
        console.info("[FixtureEdges] auth:me", {
          fixtureId,
          userId: me?.uid ?? me?.id ?? null,
          isPremium: !!me?.is_premium,
        });
      }
    } catch (error) {
      if (FIXTURE_EDGE_DEBUG) {
        console.info("[FixtureEdges] auth:me error", {
          fixtureId,
          message: error?.message || String(error),
        });
      }
    }

    if (me?.is_premium) {
      const freshAuth = await buildFirebaseAuthHeaders({ forceRefresh: true });
      retriedWithFreshToken = true;

      if (FIXTURE_EDGE_DEBUG) {
        console.info("[FixtureEdges] request:retry", {
          fixtureId,
          tokenAttached: !!freshAuth.token,
          forcedRefresh: true,
        });
      }

      res = await api.get(path, {
        headers: {
          ...freshAuth.headers,
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      });
      data = res?.data || data;

      if (FIXTURE_EDGE_DEBUG) {
        console.info("[FixtureEdges] response:retry", {
          fixtureId,
          isPremium: !!data?.is_premium,
          hasAccess: !!data?.has_access,
          usedToday: data?.used_today ?? null,
          limit: data?.limit ?? null,
        });
      }
    }
  }

  return {
    ...data,
    _auth: {
      signedIn: !!initialAuth.firebaseUser,
      tokenAttached: !!initialAuth.token,
      meIsPremium: !!me?.is_premium,
      retriedWithFreshToken,
      authMismatch:
        !!initialAuth.firebaseUser &&
        !!me?.is_premium &&
        !data?.is_premium &&
        !data?.has_access,
    },
  }; // { fixture_id, is_premium, has_access, used_today, limit, edges or edges_teaser }
};

// ----- Tipster ACCAs -----
export const createTipsterAcca = (username, payload) =>
  api
    .post(`/api/tipsters/${encodeURIComponent(username)}/accas`, payload)
    .then((r) => r.data);

export const fetchTipsterAccas = (username) =>
  api
    .get(`/api/tipsters/${encodeURIComponent(username)}/accas`)
    .then((r) => r.data);

export const settleTipsterAcca = (accaId, result) =>
  api
    .post(`/api/tipsters/accas/${accaId}/settle`, { result })
    .then((r) => r.data);

export const deleteTipsterAcca = (accaId) =>
  api.delete(`/api/tipsters/accas/${accaId}`).then((r) => r.data);

// ----- Tipsters follow/unfollow -----
export const followTipster = async (username) => {
  const res = await api.post(
    `/api/tipsters/${encodeURIComponent(username)}/follow`
  );
  return res.data;
};

export const unfollowTipster = async (username) => {
  const res = await api.delete(
    `/api/tipsters/${encodeURIComponent(username)}/follow`
  );
  return res.data;
};

// ----- Following feed -----
export const fetchFollowingList = async () =>
  api.get("/api/tipsters/following/list").then((r) => r.data);

export const fetchFollowingFeed = async () =>
  api.get("/api/tipsters/following/feed").then((r) => r.data);

// ----- Social comments (following feed + featured picks) -----
export const fetchSocialComments = async ({ target_type, target_id, limit = 50 } = {}) => {
  const res = await api.get("/api/social/comments", {
    params: {
      target_type,
      target_id,
      limit,
    },
  });
  return res.data; // { comments: [] } or []
};

export const createSocialComment = async ({ target_type, target_id, body, parent_id } = {}) => {
  const res = await api.post("/api/social/comments", {
    target_type,
    target_id,
    body,
    parent_id: parent_id ?? null,
  });
  return res.data;
};

export const deleteSocialComment = async (commentId) => {
  const res = await api.delete(`/api/social/comments/${encodeURIComponent(commentId)}`);
  return res.data;
};

export const fetchSocialReactionsSummary = async ({ target_type, target_id } = {}) => {
  const res = await api.get("/api/social/reactions/summary", {
    params: { target_type, target_id },
  });
  return res.data;
};

export const toggleSocialReaction = async ({ target_type, target_id, reaction_type = "like" } = {}) => {
  const res = await api.post("/api/social/reactions/toggle", {
    target_type,
    target_id,
    reaction_type,
  });
  return res.data;
};

// ----- Sports news -----
export const fetchSportsTalkingPoints = async ({ day, limit = 6 } = {}) => {
  const res = await api.get("/api/news/talking-points", {
    params: {
      day,
      limit,
    },
  });
  return res.data;
};

export const fetchFixtureNews = async (fixtureId, { limit = 6 } = {}) => {
  const res = await api.get(`/api/news/fixture/${encodeURIComponent(fixtureId)}`, {
    params: { limit },
  });
  return res.data;
};

export const fetchTeamNews = async (teamName, { limit = 6 } = {}) => {
  const res = await api.get("/api/news/team", {
    params: {
      team_name: teamName,
      limit,
    },
  });
  return res.data;
};

/**
 * @typedef {Object} ApiHighlightVideo
 * @property {number} id
 * @property {string} platform
 * @property {string} external_video_id
 * @property {string|null} channel_id
 * @property {string|null} channel_name
 * @property {string} title
 * @property {string|null} description
 * @property {string|null} thumbnail_url
 * @property {string|null} video_url
 * @property {string|null} published_at
 * @property {string|null} sport
 * @property {string|null} league
 * @property {string|null} team_name
 * @property {number|null} fixture_id
 * @property {Record<string, unknown>} tags
 */

/**
 * @typedef {Object} ApiHighlightVideosResponse
 * @property {number=} fixture_id
 * @property {string=} team_name
 * @property {string=} tag_value
 * @property {ApiHighlightVideo[]} videos
 */

/**
 * @typedef {Object} ApiExternalSocialPost
 * @property {number} id
 * @property {string} platform
 * @property {string} external_post_id
 * @property {string} source_handle
 * @property {string|null} source_name
 * @property {string|null} source_avatar_url
 * @property {boolean} source_verified
 * @property {string} text
 * @property {string|null} post_url
 * @property {string|null} media_url
 * @property {string|null} published_at
 * @property {string|null} sport
 * @property {string|null} league
 * @property {string|null} team_name
 * @property {number|null} fixture_id
 * @property {Record<string, unknown>} tags
 */

/**
 * @typedef {Object} ApiExternalSocialPostsResponse
 * @property {number=} fixture_id
 * @property {string=} team_name
 * @property {string=} tag_value
 * @property {ApiExternalSocialPost[]} posts
 */

export const fetchFixtureHighlights = async (fixtureId, { limit = 4, since_days = 45 } = {}) => {
  const res = await api.get(`/api/social/highlights/fixture/${encodeURIComponent(fixtureId)}`, {
    params: { limit, since_days },
  });
  return res.data;
};

export const fetchTeamHighlights = async (teamName, { limit = 18, since_days = 90 } = {}) => {
  const res = await api.get(`/api/social/highlights/team/${encodeURIComponent(teamName)}`, {
    params: { limit, since_days },
  });
  return res.data;
};

export const fetchHighlightsFeed = async ({ sport = "football", limit = 30, since_days = 30 } = {}) => {
  const res = await api.get("/api/social/highlights/feed", {
    params: { sport, limit, since_days },
  });
  return res.data;
};

export const fetchFixtureExternalMedia = async (fixtureId, { limit = 8, since_days = 14 } = {}) => {
  const res = await api.get(`/api/social/external/fixture/${encodeURIComponent(fixtureId)}`, {
    params: { limit, since_days },
  });
  return res.data;
};

export const fetchTeamExternalMedia = async (teamName, { limit = 8, since_days = 14 } = {}) => {
  const res = await api.get(`/api/social/external/team/${encodeURIComponent(teamName)}`, {
    params: { limit, since_days },
  });
  return res.data;
};

export const fetchExternalMediaFeed = async ({ sport = "football", limit = 30, since_days = 7 } = {}) => {
  const res = await api.get("/api/social/external/feed", {
    params: { sport, limit, since_days },
  });
  return res.data;
};

export const syncAdminHighlights = async ({ limit = 80, requests = 40 } = {}) => {
  const authState = await buildFirebaseAuthHeaders();
  const res = await api.post("/api/social/admin/highlights/sync", null, {
    params: { limit, requests },
    headers: authState.headers,
  });
  return res.data;
};

export const syncAdminExternalMedia = async ({ limit = 100, requests = 10 } = {}) => {
  const authState = await buildFirebaseAuthHeaders();
  const res = await api.post("/api/social/admin/external/sync", null, {
    params: { limit, requests },
    headers: authState.headers,
  });
  return res.data;
};

export const createAdminRoomPrompt = async (payload = {}) => {
  const authState = await buildFirebaseAuthHeaders();
  const res = await api.post("/api/social/admin/rooms/prompts", payload, {
    headers: authState.headers,
  });
  return res.data;
};

export const fetchAdminRoomPrompts = async (teamName, { activeOnly = false, limit = 20 } = {}) => {
  const authState = await buildFirebaseAuthHeaders();
  const res = await api.get(`/api/social/rooms/team/${encodeURIComponent(teamName)}/prompts`, {
    params: {
      active_only: activeOnly,
      limit,
    },
    headers: authState.headers,
  });
  return res.data;
};

export const listAdminRoomPrompts = async (filters = {}) => {
  const authState = await buildFirebaseAuthHeaders();
  const res = await api.get("/api/social/admin/rooms/prompts", {
    params: filters,
    headers: authState.headers,
  });
  return res.data;
};

export const getAdminRoomPrompt = async (promptId) => {
  const authState = await buildFirebaseAuthHeaders();
  const res = await api.get(`/api/social/admin/rooms/prompts/${promptId}`, {
    headers: authState.headers,
  });
  return res.data;
};

export const updateAdminRoomPrompt = async (promptId, payload = {}) => {
  const authState = await buildFirebaseAuthHeaders();
  const res = await api.patch(`/api/social/admin/rooms/prompts/${promptId}`, payload, {
    headers: authState.headers,
  });
  return res.data;
};

export const deactivateAdminRoomPrompt = async (promptId) => {
  const authState = await buildFirebaseAuthHeaders();
  const res = await api.post(`/api/social/admin/rooms/prompts/${promptId}/deactivate`, null, {
    headers: authState.headers,
  });
  return res.data;
};

export const reactivateAdminRoomPrompt = async (promptId) => {
  const authState = await buildFirebaseAuthHeaders();
  const res = await api.post(`/api/social/admin/rooms/prompts/${promptId}/reactivate`, null, {
    headers: authState.headers,
  });
  return res.data;
};

export const deleteAdminRoomPrompt = async (promptId) => {
  const authState = await buildFirebaseAuthHeaders();
  const res = await api.delete(`/api/social/admin/rooms/prompts/${promptId}`, {
    headers: authState.headers,
  });
  return res.data;
};

/**
 * @typedef {Object} ApiUserPostTag
 * @property {string} tag_type
 * @property {string} value
 * @property {string} tag_value
 * @property {string|null} team_name
 * @property {number|null} fixture_id
 * @property {string|null} sport
 */

/**
 * @typedef {Object} ApiUserPost
 * @property {number} id
 * @property {number} user_id
 * @property {string} body
 * @property {string} visibility
 * @property {string|null} created_at
 * @property {string|null} updated_at
 * @property {{id:number|null, username:string|null, display_name:string|null, avatar_url:string|null}} author
 * @property {ApiUserPostTag[]} tags
 * @property {boolean} can_delete
 */

/**
 * @typedef {Object} ApiUserPostsResponse
 * @property {ApiUserPost[]} posts
 * @property {number=} member_count
 * @property {string=} team_name
 * @property {string=} tag_value
 * @property {string|null=} sport
 */

// ----- Social posts -----
export const createSocialPost = async ({
  body,
  visibility = "public",
  team_tags = [],
  tags,
  image_url,
  repost_of_post_id,
  room_context,
} = {}) => {
  const payload = {
    body,
    visibility,
  };
  if (image_url) payload.image_url = image_url;
  if (repost_of_post_id) payload.repost_of_post_id = repost_of_post_id;
  if (room_context) payload.room_context = room_context;
  if (Array.isArray(tags)) payload.tags = tags;
  else payload.team_tags = team_tags;
  const res = await api.post("/api/social/posts", payload);
  return res.data;
};

export const fetchSocialPostsFeed = async ({ limit, following_only, for_you, cursor } = {}) => {
  const res = await api.get("/api/social/posts/feed", {
    params: {
      ...(limit ? { limit } : {}),
      ...(following_only === undefined ? {} : { following_only }),
      ...(for_you === undefined ? {} : { for_you }),
      ...(cursor === undefined ? {} : { cursor }),
    },
  });
  return res.data;
};

export const searchSocialPosts = async (query, { limit = 30 } = {}) =>
  api.get("/api/social/posts/search", { params: { q: query, limit } }).then((r) => r.data);

export const fetchSocialPost = async (postId) =>
  api.get(`/api/social/post/${encodeURIComponent(postId)}`).then((r) => r.data);

export const setSocialFeedFeedback = async (postId, action) =>
  api.post(`/api/social/posts/${encodeURIComponent(postId)}/feedback`, { action }).then((r) => r.data);

export const fetchSocialActivity = async ({ limit = 75 } = {}) =>
  api.get("/api/social/activity", { params: { limit } }).then((r) => r.data);

export const fetchSocialTrending = async ({ hours = 48, limit = 8 } = {}) =>
  api.get("/api/social/trending", { params: { hours, limit } }).then((r) => r.data);

export const fetchSocialBookmarks = async ({ limit = 150 } = {}) =>
  api.get("/api/social/bookmarks", { params: { limit } }).then((r) => r.data);

export const addSocialBookmark = async (targetType, targetId) =>
  api.post(`/api/social/bookmarks/${targetType}/${encodeURIComponent(targetId)}`).then((r) => r.data);

export const removeSocialBookmark = async (targetType, targetId) =>
  api.delete(`/api/social/bookmarks/${targetType}/${encodeURIComponent(targetId)}`).then((r) => r.data);

export const fetchMyTeamRooms = async ({ limit = 20 } = {}) =>
  api.get("/api/social/rooms/mine", { params: { limit } }).then((r) => r.data);

export const fetchTeamRoomMembership = async (teamName, { sport, league_key, league_name } = {}) =>
  api.get(`/api/social/rooms/team/${encodeURIComponent(teamName)}/membership`, {
    params: { sport, league_key, league_name },
  }).then((r) => r.data);

export const joinTeamRoom = async (teamName, { sport, league_key, league_name } = {}) =>
  api.post(`/api/social/rooms/team/${encodeURIComponent(teamName)}/join`, {
    sport, league_key, league_name,
  }).then((r) => r.data);

export const fetchTeamRoomPrompts = async (teamName, { sport, league_key, league_name, limit = 10 } = {}) =>
  api.get(`/api/social/rooms/team/${encodeURIComponent(teamName)}/prompts`, {
    params: { sport, league_key, league_name, active_only: true, limit },
  }).then((r) => r.data);

export const voteTeamRoomPrompt = async (promptId, optionId) =>
  api.post(`/api/social/rooms/prompts/${encodeURIComponent(promptId)}/vote`, { option_id: optionId }).then((r) => r.data);

export const fetchSuggestedUsers = async ({ limit = 8 } = {}) =>
  api.get("/api/users/suggestions", { params: { limit } }).then((r) => r.data);

export const reportSocialContent = async ({ target_type, target_id, reason, details }) =>
  api.post("/api/social/reports", { target_type, target_id, reason, details }).then((r) => r.data);

export const setUserModeration = async (targetUserId, action) =>
  api.post(`/api/social/moderation/users/${encodeURIComponent(targetUserId)}/${action}`).then((r) => r.data);

export const fetchUserModeration = async (targetUserId) =>
  api.get(`/api/social/moderation/users/${encodeURIComponent(targetUserId)}`).then((r) => r.data);

export const clearUserModeration = async (targetUserId, action) =>
  api.delete(`/api/social/moderation/users/${encodeURIComponent(targetUserId)}/${action}`).then((r) => r.data);

export const fetchTeamSocialPosts = async (teamName, { sport = "football", limit = 40, since_days = 180 } = {}) => {
  const res = await api.get(`/api/social/posts/team/${encodeURIComponent(teamName)}`, {
    params: { sport, limit, since_days },
  });
  return res.data;
};

export const fetchUserSocialPosts = async (identifier, { limit } = {}) => {
  const res = await api.get(`/api/social/posts/user/${encodeURIComponent(identifier)}`, {
    params: limit ? { limit } : undefined,
  });
  return res.data;
};

export const deleteSocialPost = async (postId) => {
  const res = await api.delete(`/api/social/posts/${encodeURIComponent(postId)}`);
  return res.data;
};

export const fetchFixtureSocialPosts = async (fixtureId, { limit } = {}) => {
  const res = await api.get(`/api/social/posts/fixture/${encodeURIComponent(fixtureId)}`, {
    params: limit ? { limit } : undefined,
  });
  return res.data;
};

export const fetchFixtureSocialPostTags = async (fixtureIds = []) => {
  const ids = Array.isArray(fixtureIds) ? fixtureIds.filter(Boolean).join(",") : String(fixtureIds || "");
  if (!ids) return {};
  const res = await api.get("/api/social/posts/fixture-tags", {
    params: { fixture_ids: ids },
  });
  return res.data;
};

// -----------------------------
// Billing (Stripe)
// -----------------------------

// Start Stripe Checkout for premium
export const startPremiumCheckout = async () => {
  const res = await api.post("/api/billing/premium/checkout");
  return res.data; // { checkout_url }
};

// Stripe customer billing portal (manage subscription)
export const fetchBillingPortal = async () => {
  const res = await api.post("/api/billing/customer-portal");
  return res.data; // { url }
};

// Check current billing / premium status from backend
export const fetchBillingStatus = async () => {
  const res = await api.get("/api/billing/status");
  return res.data; // e.g. { is_premium, stripe_customer_id, ... }
};

// -----------------------------
// Tipster Subscriptions (OnlyFans-style)
// -----------------------------

// Get viewer's subscription state for a tipster
export const fetchTipsterSubscription = async (username) => {
  const res = await api.get(
    `/api/tipsters/${encodeURIComponent(username)}/subscription`
  );
  return res.data;
};

// Start subscription (manual for now — will swap to Stripe Checkout)
export async function startTipsterSubscription(username) {
  const res = await api.post(
    `/api/tipsters/${encodeURIComponent(username)}/subscription/checkout`
  );
  return res.data;
}

// Cancel subscription
export const cancelTipsterSubscription = async (username) => {
  const res = await api.post(
    `/api/tipsters/${encodeURIComponent(username)}/subscription/cancel`
  );
  return res.data;
};

// ----- Tipster Stripe Connect (creator payouts) -----
export const fetchTipsterConnectStatus = (username) =>
  api
    .get(
      `/api/tipsters/${encodeURIComponent(username)}/connect/status`
    )
    .then((r) => r.data);

export const startTipsterConnectOnboarding = (username) =>
  api
    .post(
      `/api/tipsters/${encodeURIComponent(username)}/connect/onboard`
    )
    .then((r) => r.data); // { onboarding_url }

export const fetchTipsterConnectDashboard = (username) =>
  api
    .get(
      `/api/tipsters/${encodeURIComponent(username)}/connect/dashboard`
    )
    .then((r) => r.data); // { dashboard_url }

// ----- Tipster Email -----
export const sendTipsterPicksEmail = async (username) => {
  const res = await api.post(
    `/api/tipsters/${encodeURIComponent(username)}/picks/email`
  );
  return res.data; // { ok, sent_count, skipped_count, recipient_count }
};

// -----------------------------------------------------
// Freemium: daily fixture access summary (Dashboard pill)
// -----------------------------------------------------
export const fetchFixtureAccessSummary = () =>
  api.get("/fixture/access/summary").then((r) => r.data);

// -----------------------------
// Web Push / Notifications
// -----------------------------
export async function registerPushToken(token, platform = "web") {
  const res = await api.post("/api/push/register", { token, platform });
  return res.data;
}

// -----------------------------
// User Preferences (favourites)
// -----------------------------
export async function getMyPreferences() {
  const res = await api.get("/api/me/preferences");
  return res.data; // { favorite_teams: [], favorite_leagues: [] }
}

export async function saveMyPreferences(prefs) {
  const res = await api.post("/api/me/preferences", prefs);
  return res.data;
}

// -----------------------------
// User Following (not just tipsters)
// -----------------------------

// Follow a user by username or firebase_uid
export const followUser = async (identifier) => {
  const res = await api.post(`/api/users/${encodeURIComponent(identifier)}/follow`);
  return res.data;
};

// Unfollow a user
export const unfollowUser = async (identifier) => {
  const res = await api.delete(`/api/users/${encodeURIComponent(identifier)}/follow`);
  return res.data;
};

// Get list of users I'm following
export const fetchFollowingUsers = async () => {
  const res = await api.get("/api/users/following");
  return res.data; // array of user objects
};

// Get list of my followers
export const fetchMyFollowers = async () => {
  const res = await api.get("/api/users/followers");
  return res.data; // array of user objects
};

// Check if I'm following a specific user
export const checkFollowingUser = async (identifier) => {
  const res = await api.get(`/api/users/${encodeURIComponent(identifier)}/follow/status`);
  return res.data; // { is_following: true/false }
};

// -----------------------------
// User Privacy Settings
// -----------------------------

// Get my privacy settings
export const getPrivacySettings = async () => {
  const res = await api.get("/api/me/privacy");
  return res.data; // { bet_tracker_visibility: "public" | "followers" | "private" }
};

// Update privacy settings
export const updatePrivacySettings = async (settings) => {
  const res = await api.post("/api/me/privacy", settings);
  return res.data;
};

// -----------------------------
// User Bet Tracker (viewing others)
// -----------------------------

// Get another user's bet tracker (respects privacy)
export const fetchUserBets = async (identifier) => {
  const res = await api.get(`/api/users/${encodeURIComponent(identifier)}/bets`);
  return res.data; // array of bets (if allowed to view)
};

// Get user profile by username or firebase_uid
export const fetchUserProfile = async (identifier) => {
  const res = await api.get(`/api/users/${encodeURIComponent(identifier)}/profile`);
  return res.data; // { username, display_name, avatar_url, bet_stats, etc. }
};

// -----------------------------
// Tipster Chat (Subscriber Community)
// -----------------------------

// Get chat messages for a tipster (paginated)
export async function fetchTipsterChat(tipsterId, { limit = 50, beforeId } = {}) {
  const params = { limit };
  if (beforeId) params.before_id = beforeId;
  const res = await api.get(`/api/tipster/${tipsterId}/chat`, { params });
  return res.data; // { messages, total, has_more, pinned }
}

// Send a message to tipster's chat
export async function sendTipsterChatMessage(tipsterId, { content, pickId, messageType = "message" }) {
  const res = await api.post(`/api/tipster/${tipsterId}/chat`, {
    content,
    pick_id: pickId || null,
    message_type: messageType,
  });
  return res.data;
}

// Edit a chat message
export async function editTipsterChatMessage(tipsterId, messageId, content) {
  const res = await api.put(`/api/tipster/${tipsterId}/chat/${messageId}`, { content });
  return res.data;
}

// Delete a chat message
export async function deleteTipsterChatMessage(tipsterId, messageId) {
  const res = await api.delete(`/api/tipster/${tipsterId}/chat/${messageId}`);
  return res.data;
}

// Pin/unpin a chat message
export async function togglePinTipsterChatMessage(tipsterId, messageId) {
  const res = await api.post(`/api/tipster/${tipsterId}/chat/${messageId}/pin`);
  return res.data;
}

// Get chat stats
export async function fetchTipsterChatStats(tipsterId) {
  const res = await api.get(`/api/tipster/${tipsterId}/chat/stats`);
  return res.data;
}

// =============================================================================
// PUBLIC COMMUNITY CHAT (Premium users only)
// =============================================================================

// Get public chat messages (paginated)
export async function fetchPublicChat({ limit = 50, beforeId } = {}) {
  const params = { limit };
  if (beforeId) params.before_id = beforeId;
  const res = await api.get(`/api/community/chat`, { params });
  return res.data; // { messages, total, has_more, pinned }
}

// Send a message to public chat
export async function sendPublicChatMessage({ content, fixtureId, messageType = "message" }) {
  const res = await api.post(`/api/community/chat`, {
    content,
    fixture_id: fixtureId || null,
    message_type: messageType,
  });
  return res.data;
}

// Edit a public chat message
export async function editPublicChatMessage(messageId, content) {
  const res = await api.put(`/api/community/chat/${messageId}`, { content });
  return res.data;
}

// Delete a public chat message
export async function deletePublicChatMessage(messageId) {
  const res = await api.delete(`/api/community/chat/${messageId}`);
  return res.data;
}

// Pin/unpin a public chat message (admin only)
export async function togglePinPublicChatMessage(messageId) {
  const res = await api.post(`/api/community/chat/${messageId}/pin`);
  return res.data;
}

// Get public chat stats
export async function fetchPublicChatStats() {
  const res = await api.get(`/api/community/chat/stats`);
  return res.data;
}

// =============================================================================
// GLOBAL SEARCH
// =============================================================================

// Search for users, teams, and leagues
export async function globalSearch(query, limit = 5) {
  const res = await api.get("/api/search", {
    params: { q: query, limit }
  });
  return res.data; // { users: [], teams: [], leagues: [] }
}

// =============================================================================
// LEAGUE PAGES
// =============================================================================

const toLeagueParam = (leagueCode) =>
  isWorldCupCompetition(String(leagueCode || ""))
    ? WORLD_CUP_CODE
    : String(leagueCode || "")
        .trim()
        .toUpperCase();

// Fetch league standings table
export async function fetchLeagueTable(leagueCode, season) {
  const league = toLeagueParam(leagueCode);
  const resolvedSeason = league === WORLD_CUP_CODE ? WORLD_CUP_SEASON : season;
  const currentParams = { league };
  const leagueCodeParams = { league_code: league };
  if (resolvedSeason !== undefined && resolvedSeason !== null && resolvedSeason !== "") {
    currentParams.season = resolvedSeason;
    leagueCodeParams.season = resolvedSeason;
  }
  if (league === "WC") {
    try {
      const res = await api.get(`/api/league/table`, {
        params: leagueCodeParams
      });
      return res.data;
    } catch {
      const res = await api.get(`/api/league/table`, {
        params: currentParams
      });
      return res.data;
    }
  }
  try {
    const res = await api.get(`/api/fixtures/league/table`, {
      params: currentParams
    });
    return res.data; // { league, league_name, season, table: [] }
  } catch {
    try {
      const res = await api.get(`/api/league/table`, {
        params: leagueCodeParams
      });
      return res.data;
    } catch {
      const res = await api.get(`/api/league/table`, {
        params: currentParams
      });
      return res.data;
    }
  }
}

// Fetch all fixtures for a league/season
export async function fetchLeagueFixtures(leagueCode, season) {
  const league = toLeagueParam(leagueCode);
  const resolvedSeason = league === WORLD_CUP_CODE ? WORLD_CUP_SEASON : season;
  const params = { league_code: league };
  if (resolvedSeason !== undefined && resolvedSeason !== null && resolvedSeason !== "") params.season = resolvedSeason;
  try {
    const res = await api.get(`/api/league/fixtures`, {
      params
    });
    return res.data; // { league_code, league_id, season, total_fixtures, fixtures: [] }
  } catch {
    const fallbackParams = { league };
    if (resolvedSeason !== undefined && resolvedSeason !== null && resolvedSeason !== "") fallbackParams.season = resolvedSeason;
    const res = await api.get(`/api/league/fixtures`, {
      params: fallbackParams
    });
    return res.data;
  }
}

// Fetch league stats/leaders for a league page
export async function fetchLeagueStats(leagueCode, season) {
  const league = toLeagueParam(leagueCode);
  const params = { league_code: league };
  if (season !== undefined && season !== null && season !== "") params.season = season;
  const res = await api.get(`/api/league/stats`, {
    params
  });
  return res.data;
}

// Fetch top scorers for a league
export async function fetchTopScorers(leagueId, season) {
  const params = { league_id: leagueId };
  if (season !== undefined && season !== null && season !== "") params.season = season;
  const res = await api.get(`/api/club/topscorers`, {
    params
  });
  return res.data;
}

// =============================================================================
// SIGNALS
// =============================================================================

export async function fetchSignalStrategies() {
  const res = await api.get("/api/signals/strategies");
  return res.data;
}

export async function fetchSignalStrategyParams(strategyKey) {
  const res = await api.get(`/api/signals/strategies/${encodeURIComponent(strategyKey)}/params`);
  return res.data;
}

export async function updateSignalStrategyParams(strategyKey, payload) {
  const res = await api.put(`/api/signals/strategies/${encodeURIComponent(strategyKey)}/params`, payload);
  return res.data;
}

export async function fetchSignalFeed({
  sport = "football",
  strategy_key = "__all_active__",
  include_watchlist = 1,
} = {}) {
  const res = await api.get("/api/signals/feed", {
    params: { sport, strategy_key, include_watchlist },
  });
  return res.data;
}

export async function createSignalPosition(payload) {
  const res = await api.post("/api/signals/positions", payload);
  return res.data;
}

export async function fetchSignalPositions() {
  const res = await api.get("/api/signals/positions");
  return res.data;
}

export async function closeSignalPosition(positionId, payload) {
  const res = await api.post(`/api/signals/positions/${positionId}/close`, payload);
  return res.data;
}

export async function fetchSignalPreferences() {
  const res = await api.get("/api/signals/preferences");
  return res.data;
}

export async function updateSignalPreferences(payload) {
  const res = await api.put("/api/signals/preferences", payload);
  return res.data;
}

export async function fetchSignalsPerformance({
  days_back = 30,
  sport = "football",
  strategy_key,
} = {}) {
  const params = { days_back, sport };
  if (strategy_key) params.strategy_key = strategy_key;
  const res = await api.get("/api/signals/performance", { params });
  return res.data;
}

export async function fetchSignalsBacktests({
  days_back = 90,
  strategy_key,
} = {}) {
  const params = { days_back };
  if (strategy_key) params.strategy_key = strategy_key;
  const res = await api.get("/api/signals/backtests", { params });
  return res.data;
}

export async function adminScanLiveSignals({
  strategy_key = "__all_active__",
  notify_premium = 1,
  include_all_leagues = 0,
  max_staleness_sec = 180,
  include_stopped = 0,
} = {}) {
  const res = await api.post("/api/signals/scan-live", null, {
    params: {
      strategy_key,
      notify_premium,
      include_all_leagues,
      max_staleness_sec,
      include_stopped,
    },
  });
  return res.data;
}

export async function adminMonitorSignalExits() {
  const res = await api.post("/api/signals/monitor-exits");
  return res.data;
}

export async function fetchAdminContentReports(status = "open") {
  const res = await api.get("/api/social/admin/reports", { params: { status, limit: 200 } });
  return res.data;
}

export async function reviewAdminContentReport(reportId, payload) {
  const res = await api.patch(`/api/social/admin/reports/${reportId}`, payload);
  return res.data;
}
